import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import {
  bulkApproveDutyLines,
  BulkApproveDutyLinesOptions,
  calculateDutyLines,
  CalculateDutyLinesOptions,
  generateRealizationReport,
  realizeDutyLines,
  RealizeDutyLinesOptions
} from 'RestAPIs/admin';

import showSnackbar, { showErrorSnackbar } from 'components/shared/Snackbar';
import { ReportDownloadingWSData } from '../Reports/type';
import { useEffect } from 'react';

type ReportBody = ReportDownloadingWSData['body'];

export const useRecalculateDutyLinesMutation = () => {
  const { t } = useTranslation();
  return useMutation(
    (options: CalculateDutyLinesOptions) => calculateDutyLines(options),
    {
      onSuccess: () =>
        showSnackbar({
          severity: 'success',
          message: t('adminPortal.realization.recalculateDuties.success')
        }),
      onError: () =>
        showSnackbar({
          severity: 'error',
          message: t('adminPortal.realization.recalculateDuties.error')
        })
    }
  );
};

export const useRealizeDutyLinesMutation = () => {
  const { t } = useTranslation();
  return useMutation(
    (options: RealizeDutyLinesOptions) => realizeDutyLines(options),
    {
      onSuccess: () =>
        showSnackbar({
          severity: 'success',
          message: t('adminPortal.realization.realizationProcedure.success')
        }),
      onError: () =>
        showSnackbar({
          severity: 'error',
          message: t('adminPortal.realization.realizationProcedure.error')
        })
    }
  );
};

export const useBulkApproveDutyLinesMutation = () => {
  const { t } = useTranslation();
  return useMutation(
    (options: BulkApproveDutyLinesOptions) => bulkApproveDutyLines(options),
    {
      onSuccess: () =>
        showSnackbar({
          severity: 'success',
          message: t('adminPortal.realization.bulkApproval.success')
        }),
      onError: () =>
        showSnackbar({
          severity: 'error',
          message: t('adminPortal.realization.bulkApproval.error')
        })
    }
  );
};

export const useDownloadRealizationReport = ({ validFrom, validTo }) => {
  const REPORT_GENERATION_TIMEOUT = 30000;

  const downloadReport: (
    evt: Event,
    resolve: (data: ReportBody) => void
  ) => void = (event: Event, resolve: (data: ReportBody) => void) => {
    const customEvent = event as CustomEvent<ReportBody>;
    resolve(customEvent.detail);
  };

  const dojoReactReportResponse = () =>
    new Promise<ReportBody>((resolve, reject) => {
      const timer = setTimeout(reject, REPORT_GENERATION_TIMEOUT);

      window.addEventListener('dojoreactreport', (event) => {
        clearTimeout(timer);
        downloadReport(event, resolve);
      });
    });

  // Kill event listener on unmount
  useEffect(
    () => () =>
      window.removeEventListener('dojoreactreport', dojoReactReportResponse)
  );

  return useQuery(
    ['generateRealizationReport', validFrom, validTo],
    async () => {
      try {
        await generateRealizationReport({ validFrom, validTo });
        return await dojoReactReportResponse();
      } catch (err) {
        showErrorSnackbar();
      }
    },
    {
      enabled: false
    }
  );
};
